import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import CommentsSection from "./Comments";
import { apiCall } from "./api";
import { SHAPE_TYPE, VISIBILITY } from "./utils/constants";
import Toastr from "./Toastr";
import { getUserEmail, getUserName } from "./utils/map.util";
import { buildPostsCsv } from "./utils/postsCsv";

const baseHeadCells = [
  { id: "action", label: "Action", sortable: false },
  { id: "locationName", label: "Location", sortable: true },
  { id: "userName", label: "User Name", sortable: true },
  { id: "userEmail", label: "Email", sortable: true },
  { id: "visibility", label: "Visibility", sortable: true },
  { id: "lastUpdated", label: "Last Updated", sortable: true },
  { id: "approved", label: "Approved", sortable: true },
  { id: "reliable", label: "Reliable", sortable: true },
];

const VISIBILITY_LABELS = {
  [VISIBILITY.PRIVATE]: "Private",
  [VISIBILITY.PROJECT]: "Project",
  [VISIBILITY.PUBLIC]: "Public",
};

const VISIBILITY_BADGE_STYLES = {
  [VISIBILITY.PRIVATE]: {
    color: "var(--theme-danger-clr)",
    borderColor: "var(--theme-danger-clr)",
  },
  [VISIBILITY.PROJECT]: {
    color: "var(--theme-color)",
    borderColor: "var(--theme-color)",
  },
  [VISIBILITY.PUBLIC]: {
    color: "var(--theme-success-clr)",
    borderColor: "var(--theme-success-clr)",
  },
};

const getPolygonVertexCount = (loc_id) => {
  if (!loc_id) return 0;
  let coords = loc_id;
  if (typeof loc_id === "string") {
    try {
      coords = JSON.parse(loc_id);
    } catch (e) {
      return 0;
    }
  }
  if (!Array.isArray(coords) || coords.length === 0) return 0;
  const first = coords[0];
  const last = coords[coords.length - 1];
  const isClosed =
    Array.isArray(first) &&
    Array.isArray(last) &&
    first[0] === last[0] &&
    first[1] === last[1] &&
    coords.length > 1;
  return isClosed ? coords.length - 1 : coords.length;
};

const getLocationName = (row) => {
  const fields = row?.fields || [];
  const headerField = fields.find((field) => field.field_type === "header");
  if (headerField?.field_value) {
    return headerField.field_value;
  }

  if (row?.shape_type === SHAPE_TYPE.POLYGON) {
    const n = getPolygonVertexCount(row.loc_id);
    return n > 0
      ? `User Defined Region (${n}-point polygon)`
      : "User Defined Region (polygon)";
  }

  if (row?.shape_type === SHAPE_TYPE.LINE) {
    const n = getPolygonVertexCount(row.loc_id);
    return n > 0
      ? `User Defined Line (${n}-point line)`
      : "User Defined Line";
  }

  const addressField = fields.find((field) => field.field_type === "address");
  if (addressField?.field_value) {
    return addressField.field_value;
  }

  const nameField = fields.find((field) =>
    String(field.field_name || "")
      .toLowerCase()
      .includes("name"),
  );
  if (nameField?.field_value) {
    return nameField.field_value;
  }

  return "Unknown";
};


const getDateValue = (row) => {
  const raw =
    row?.modified_at || row?.updated_at || row?.created_at || null;
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const VISIBILITY_SORT_ORDER = {
  [VISIBILITY.PRIVATE]: 0,
  [VISIBILITY.PROJECT]: 1,
  [VISIBILITY.PUBLIC]: 2,
};

const getVisibilitySortValue = (row) =>
  VISIBILITY_SORT_ORDER[row?.visibility] ?? 99;

const getApprovalValue = (row) => (row?.approval_status === "approved" ? 1 : 0);

const getReliableValue = (row) => (row?.is_reliable ? 1 : 0);

const getActionValue = (row) => String(row?.id ?? "");

const compareValues = (a, b, orderBy) => {
  if (orderBy === "lastUpdated" || orderBy === "dateAdded") {
    const dateA = getDateValue(a);
    const dateB = getDateValue(b);
    const timeA = dateA ? dateA.getTime() : 0;
    const timeB = dateB ? dateB.getTime() : 0;
    return timeA - timeB;
  }

  if (orderBy === "userName") {
    return getUserName(a).localeCompare(getUserName(b));
  }

  if (orderBy === "userEmail") {
    return getUserEmail(a).localeCompare(getUserEmail(b));
  }

  if (orderBy === "locationName") {
    return getLocationName(a).localeCompare(getLocationName(b));
  }

  if (orderBy === "visibility") {
    return getVisibilitySortValue(a) - getVisibilitySortValue(b);
  }

  if (orderBy === "approved") {
    return getApprovalValue(a) - getApprovalValue(b);
  }

  if (orderBy === "reliable") {
    return getReliableValue(a) - getReliableValue(b);
  }

  if (orderBy === "action") {
    return getActionValue(a).localeCompare(getActionValue(b));
  }

  return getActionValue(a).localeCompare(getActionValue(b));
};

const LocationPostsTable = ({
  project,
  user,
  onBackToMap,
  onRowSelect,
  highlightedSubmissionId = null,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const rowsPerPage = 10;
  const [orderBy, setOrderBy] = useState("lastUpdated");
  const [order, setOrder] = useState("desc");
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [activeSubmissionId, setActiveSubmissionId] = useState(null);
  const [, setUpdatingIds] = useState({});
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    const fetchPosts = async () => {
      if (!project?.projectsid) return;
      setLoading(true);

      try {
        const params = new URLSearchParams({
          projectsid: project.projectsid,
          limit: rowsPerPage,
          page: page + 1,
        });

        if (searchTerm.trim()) {
          params.set("query", searchTerm.trim());
        }

        // Bug fix (table-private): previously used raw fetch() which omitted
        // the Authorization: Bearer <jwt> header. listProjectPosts.php derives
        // role from the verified Firebase token, so without the header admins
        // and moderators silently fell through to the public-only branch and
        // private posts never appeared in the table. apiCall() injects the
        // token (and refreshes on 401) so the visibility matrix runs against
        // the real caller.
        const data = await apiCall(`listProjectPosts.php?${params.toString()}`);

        setRows(Array.isArray(data.posts) ? data.posts : []);
        setTotalCount(Number.isFinite(data.total) ? data.total : 0);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [project?.projectsid, user?.uid, page, searchTerm]);

  const sortedRows = useMemo(() => {
    const stabilized = rows.map((row, index) => [row, index]);
    stabilized.sort((a, b) => {
      const comparison = compareValues(a[0], b[0], orderBy);
      if (comparison !== 0) {
        return order === "asc" ? comparison : -comparison;
      }
      return a[1] - b[1];
    });
    return stabilized.map((item) => item[0]);
  }, [rows, orderBy, order]);

  const headCells = baseHeadCells;

  const handleRequestSort = (property) => {
    if (!property) return;
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleOpenComments = (submissionId) => {
    setActiveSubmissionId(submissionId);
    setCommentDialogOpen(true);
  };

  const handleCloseComments = () => {
    setCommentDialogOpen(false);
    setActiveSubmissionId(null);
  };

  const handleRowClick = (row) => {
    if (!row) return;
    onRowSelect?.(row);
  };

  const handleActionMenuOpen = (event, row) => {
    event.stopPropagation();
    setActionAnchorEl(event.currentTarget);
    setActionRow(row);
  };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  const [exporting, setExporting] = useState(false);

  const handleExportCsv = async () => {
    if (!project?.projectsid || exporting) return;
    setExporting(true);
    try {
      const exportParams = new URLSearchParams({
        projectsid: project.projectsid,
        // backend caps export at 1000 (see listProjectPosts.php). Anything
        // larger would either need pagination here or a deeper backend
        // change; flag in the toast if we hit the ceiling.
        limit: 1000,
        page: 1,
        export: 1,
      });
      if (searchTerm.trim()) {
        exportParams.set("query", searchTerm.trim());
      }
      const data = await apiCall(
        `listProjectPosts.php?${exportParams.toString()}`,
      );
      const posts = Array.isArray(data?.posts) ? data.posts : [];
      const total = Number.isFinite(data?.total) ? data.total : posts.length;

      const { csv } = buildPostsCsv({
        posts,
        getLocationName,
        getUserName,
        getUserEmail,
        visibilityLabels: VISIBILITY_LABELS,
      });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const slug = (project?.project_name || `project-${project.projectsid}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `${slug}-posts-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const truncated = posts.length < total;
      setToast({
        open: true,
        severity: truncated ? "warning" : "success",
        message: truncated
          ? `Exported ${posts.length} of ${total} posts (server cap reached). Narrow with search to capture the rest.`
          : `Exported ${posts.length} post${posts.length === 1 ? "" : "s"} to CSV.`,
      });
    } catch (error) {
      console.error("CSV export failed:", error);
      setToast({
        open: true,
        severity: "error",
        message: "CSV export failed. Check console for details.",
      });
    } finally {
      setExporting(false);
    }
  };

  const updateSubmissionStatus = async (submissionId, updates, shapeType) => {
    setUpdatingIds((prev) => ({ ...prev, [submissionId]: true }));
    try {
      const normalizedUpdates = { ...updates };
      if (
        Object.prototype.hasOwnProperty.call(normalizedUpdates, "is_reliable")
      ) {
        normalizedUpdates.is_reliable = Boolean(normalizedUpdates.is_reliable);
      }
      const normalizedShapeType = shapeType || SHAPE_TYPE.MARKER;
      const result = await apiCall("updateSubmissionStatus.php", {
        method: "POST",
        body: {
          submission_id: submissionId,
          shape_type: normalizedShapeType,
          ...normalizedUpdates,
        },
      });
      if (!result?.success) {
        throw new Error(result.error || "Update failed");
      }
      setRows((prev) =>
        prev.map((row) =>
          row.id === submissionId && row.shape_type === normalizedShapeType
            ? { ...row, ...normalizedUpdates }
            : row,
        ),
      );
      const message =
        "approval_status" in normalizedUpdates
          ? "Approval status updated successfully."
          : "is_reliable" in normalizedUpdates
            ? "Reliability updated successfully."
            : "Submission updated successfully.";
      setToast({ open: true, message, severity: "success" });
    } catch (error) {
      console.error("Error updating submission status:", error);
      setToast({
        open: true,
        message: "Failed to update submission status.",
        severity: "error",
      });
    } finally {
      setUpdatingIds((prev) => ({ ...prev, [submissionId]: false }));
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--theme-bg-header)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "var(--theme-color)" }}
        >
          Manage Locations
        </Typography>
        <Button
          variant="contained"
          onClick={onBackToMap}
          sx={{
            backgroundColor: "#fff !important",
            color: "var(--theme-color) !important",
            fontWeight: "900 !important",
            textTransform: "unset !important",
            lineHeight: "unset",
          }}
        >
          Map View
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          label="Search by keyword or Name"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              setPage(0);
              setSearchTerm(searchInput.trim());
            }
            if (event.key === "Escape") {
              event.preventDefault();
              setSearchInput("");
              setSearchTerm("");
              setPage(0);
            }
          }}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {searchInput.length > 0 && (
                  <IconButton
                    aria-label="clear search"
                    onClick={() => {
                      setSearchInput("");
                      setSearchTerm("");
                      setPage(0);
                    }}
                    size="medium"
                  >
                    <ClearIcon fontSize="medium" />
                  </IconButton>
                )}
                <IconButton
                  aria-label="search"
                  onClick={() => {
                    setPage(0);
                    setSearchTerm(searchInput.trim());
                  }}
                  size="medium"
                >
                  <SearchIcon fontSize="medium" />
                </IconButton>
              </Box>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 320 },
            maxWidth: "100%",
            flex: { xs: "1 1 100%", sm: "0 1 320px" },
            "& .MuiInputLabel-root": { color: "var(--theme-color)" },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "var(--theme-color)",
            },
            "& .MuiOutlinedInput-root": {
              color: "var(--theme-color)",
              backgroundColor: "var(--theme-btn-clr)",
              "& fieldset": {
                borderColor: "var(--theme-color)",
              },
              "&:hover fieldset": {
                borderColor: "var(--theme-color)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "var(--theme-color)",
              },
              "& input": {
                fontWeight: 300,
              },
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleExportCsv}
            disabled={exporting || loading || totalCount === 0}
            sx={{
              color: "var(--theme-color)",
              borderColor: "var(--theme-color)",
              textTransform: "none",
              "&:hover": {
                borderColor: "var(--theme-color)",
                backgroundColor: "rgb(96 58 54 / 11%)",
              },
            }}
          >
            {exporting ? "Exporting…" : "Download CSV"}
          </Button>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            sx={{
              color: "var(--theme-color)",
              alignSelf: { xs: "flex-start", sm: "center" },
              "& .MuiTablePagination-selectLabel": {
                color: "var(--theme-color)",
              },
              "& .MuiTablePagination-displayedRows": {
                color: "var(--theme-color)",
              },
            }}
          />
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          p: 0,
          backgroundColor: "var(--theme-btn-clr)",
          border: "1px solid var(--theme-color)",
          borderRadius: 2,
          overflowY: "auto",
          overflowX: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "var(--theme-color)",
                    fontWeight: 700,
                    backgroundColor: "var(--theme-bg-header)",
                    borderBottom: "1px solid var(--theme-color)",
                  }}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id)}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "var(--theme-color)",
                        "&.Mui-active": {
                          color: "var(--theme-color)",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "var(--theme-color) !important",
                        },
                      }}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {headCells.map((cell) => (
                      <TableCell key={cell.id}>
                        <Skeleton variant="text" height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : sortedRows.map((row) => {
                  const dateValue = getDateValue(row);
                  const dateLabel = dateValue
                    ? dateValue.toLocaleString()
                    : "Unknown";
                  // const isPrivate = row.visibility === VISIBILITY.PRIVATE;
                  const isApproved = row.approval_status === "approved";
                  const isReliable = Boolean(row.is_reliable);
                  const isOpenInSidePanel =
                    highlightedSubmissionId != null &&
                    String(row.id) === String(highlightedSubmissionId);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleRowClick(row)}
                      title="Click to View Details"
                      sx={{
                        cursor: "pointer",
                        ...(isOpenInSidePanel && {
                          backgroundColor: "rgba(96, 58, 54, 0.12) !important",
                          boxShadow: "inset 4px 0 0 0 var(--theme-color)",
                          "&:hover": {
                            backgroundColor: "rgba(96, 58, 54, 0.16) !important",
                          },
                        }),
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <IconButton
                            aria-label="row actions"
                            onClick={(event) =>
                              handleActionMenuOpen(event, row)
                            }
                            sx={{
                              color: "var(--theme-color)",
                              borderColor: "var(--theme-color)",
                              border: "1px solid var(--theme-color)",
                              borderRadius: "4px",
                              "&:hover": {
                                borderColor: "var(--theme-color)",
                                backgroundColor: "rgb(96 58 54 / 11%)",
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "var(--theme-color)",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          maxWidth: 100,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.25,
                          }}
                        >
                          <Box component="span">{getLocationName(row)}</Box>
                          {row?.shape_type === SHAPE_TYPE.POLYGON && (
                            <Box
                              component="span"
                              sx={{
                                alignSelf: "flex-start",
                                px: 0.75,
                                py: 0,
                                borderRadius: "4px",
                                border: "1px solid var(--theme-color)",
                                fontSize: "10px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                color: "var(--theme-color)",
                              }}
                            >
                              Region
                            </Box>
                          )}
                          {row?.shape_type === SHAPE_TYPE.LINE && (
                            <Box
                              component="span"
                              sx={{
                                alignSelf: "flex-start",
                                px: 0.75,
                                py: 0,
                                borderRadius: "4px",
                                border: "1px solid var(--theme-color)",
                                fontSize: "10px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                color: "var(--theme-color)",
                              }}
                            >
                              Line
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "var(--theme-color)" }}>
                        {getUserName(row)}
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: "12px",
                            color: "var(--theme-color)",
                            opacity: 0.7,
                          }}
                        >
                          ({row?.user.display_name ? "Logged In" : "Public"})
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: "var(--theme-color)" }}>
                        {getUserEmail(row)}
                      </TableCell>
                      <TableCell sx={{ color: "var(--theme-color)" }}>
                        {(() => {
                          const visibilityKey = row?.visibility;
                          const label =
                            VISIBILITY_LABELS[visibilityKey] || "—";
                          const badgeStyle =
                            VISIBILITY_BADGE_STYLES[visibilityKey] || {
                              color: "var(--theme-color)",
                              borderColor: "var(--theme-color)",
                            };
                          return (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                px: 1,
                                py: 0.25,
                                borderRadius: "4px",
                                border: "1px solid",
                                fontSize: "12px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                ...badgeStyle,
                              }}
                            >
                              {label}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ color: "var(--theme-color)" }}>
                        {dateLabel}
                      </TableCell>
                      <TableCell sx={{ color: "var(--theme-color)" }}>
                        {isApproved ? (
                          <TaskAltIcon
                            fontSize="medium"
                            sx={{ color: "var(--theme-success-clr)" }}
                          />
                        ) : (
                          <HighlightOffIcon
                            fontSize="medium"
                            sx={{ color: "var(--theme-danger-clr)" }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "var(--theme-color)" }}>
                        {isReliable ? (
                          <TaskAltIcon
                            fontSize="medium"
                            sx={{ color: "var(--theme-success-clr)" }}
                          />
                        ) : (
                          <HighlightOffIcon
                            fontSize="medium"
                            sx={{ color: "var(--theme-danger-clr)" }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            {!loading && sortedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length}>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--theme-color)" }}
                  >
                    No result found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={commentDialogOpen}
        onClose={handleCloseComments}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--theme-btn-clr)",
            border: "1px solid var(--theme-color)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            color: "var(--theme-color)",
            fontWeight: 700,
            borderBottom: "1px solid var(--theme-color)",
          }}
        >
          Comments
          <IconButton
            aria-label="close"
            onClick={handleCloseComments}
            sx={{ marginLeft: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ backgroundColor: "var(--theme-bg-header)" }}
        >
          {activeSubmissionId ? (
            <CommentsSection
              submission_id={activeSubmissionId}
              setLeaveCommentOpen={true}
            />
          ) : (
            <Typography variant="body2">No post selected.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: "var(--theme-btn-clr)",
            border: "1px solid var(--theme-color)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionRow?.id) {
              handleOpenComments(actionRow.id);
            }
            handleActionMenuClose();
          }}
          sx={{ color: "var(--theme-color)" }}
        >
          Add Comment
        </MenuItem>
        <MenuItem
          disabled={actionRow?.visibility === VISIBILITY.PRIVATE}
          onClick={() => {
            if (!actionRow?.id) return;
            const isApproved = actionRow.approval_status === "approved";
            updateSubmissionStatus(actionRow.id, {
              approval_status: isApproved ? "pending" : "approved",
            }, actionRow.shape_type);
            handleActionMenuClose();
          }}
          sx={{ color: "var(--theme-color)" }}
        >
          {actionRow?.approval_status === "approved" ? "Disapprove" : "Approve"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionRow?.id) return;
            const isReliable = Boolean(actionRow?.is_reliable);
            updateSubmissionStatus(actionRow.id, {
              is_reliable: isReliable ? 0 : 1,
            }, actionRow.shape_type);
            handleActionMenuClose();
          }}
          sx={{ color: "var(--theme-color)" }}
        >
          {actionRow?.is_reliable ? "Unmark Reliable" : "Mark Reliable"}
        </MenuItem>
      </Menu>
      <Toastr
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setToast((prev) => ({ ...prev, open: false }));
        }}
      />
    </Box>
  );
};

export default LocationPostsTable;
